import { useState, useEffect, useCallback } from 'react'
import moment from 'moment-timezone'
import { apiEndpoints, dateFormats } from '../consts'
import { toExclusiveEndDateISO } from '../services/date'
import logger from '../utils/logger'
import { transformCallRow } from '../utils/callTransformUtils'
import { authenticatedFetch } from '../services/api/apiClient'

import {
  fetchChartData,
  fetchMetrics,
  fetchPlanSize
} from '../services/bff'

/**
 * Custom hook to handle all data fetching and processing for the dashboard
 * Centralizes API calls and data state management
 */
export function useDashboardData (
  startDate,
  endDate,
  startDateString,
  endDateString,
  company,
  campaignFilter
) {
  const [planSize, setPlanSize] = useState(null)
  const [planSizeLoading, setPlanSizeLoading] = useState(false)
  const [actualCampaignName, setActualCampaignName] = useState('')
  const [metrics, setMetrics] = useState(null)
  const [metricsLoading, setMetricsLoading] = useState(false)
  const [reportsLoaded, setReportsLoaded] = useState(false)
  const [chartData, setChartData] = useState({
    callsPerDay: [],
    handleTimePerDay: [],
    cssPerDay: [],
    dispositionBreakdown: [],
    callDirection: []
  })
  const [processedReportData, setProcessedReportData] = useState({
    rowsList: [],
    dispositionMap: {},
    timeString: 0
  })

  useEffect(() => {
    if (company) {
      setPlanSizeLoading(true);

      (async () => {
        try {
          const data = await fetchPlanSize(company)
          setPlanSize(data.planSize)
        } catch (error) {
          logger.error('Error fetching plan size:', error)
        } finally {
          setPlanSizeLoading(false)
        }
      })()
    }
  }, [company])

  const getChartData = useCallback(async () => {
    try {
      const data = await fetchChartData(startDateString, endDateString, campaignFilter)
      setChartData(data)
    } catch (error) {
      logger.error('Error fetching chart data:', error)
      setChartData({
        callsPerDay: [],
        handleTimePerDay: [],
        cssPerDay: [],
        dispositionBreakdown: [],
        callDirection: []
      })
    }
  }, [startDateString, endDateString, campaignFilter])

  useEffect(() => {
    if (startDateString && endDateString) {
      getChartData()
    }
  }, [getChartData, startDateString, endDateString])

  useEffect(() => {
    if (new Date(startDate) > new Date(endDate)) return

    const loadReportsAndMetrics = async () => {
      setMetricsLoading(true)
      setReportsLoaded(false)
      try {
        const adjustedStartDate = moment(startDate).tz(dateFormats.timezone).startOf('day').toDate()
        const adjustedEndDateISO = toExclusiveEndDateISO(endDate)

        logger.debug('Fetching calls from database', { campaignFilter, startDate: adjustedStartDate, endDate: adjustedEndDateISO })

        const start = adjustedStartDate.toISOString()
        const end = adjustedEndDateISO

        let url
        if (campaignFilter) {
          const encodedName = encodeURIComponent(campaignFilter)
          url = `${apiEndpoints.callsByCampaign(encodedName)}?start=${start}&end=${end}`
        } else {
          url = `${apiEndpoints.calls}?startDate=${start}&endDate=${end}&limit=1000&sortField=call_date&sortDirection=DESC`
        }

        const response = await authenticatedFetch(url)

        if (response.ok) {
          const data = await response.json()
          const calls = Array.isArray(data) ? data : (data.calls || [])

          logger.debug(`Fetched ${calls.length} calls from database`, { campaignFilter: campaignFilter || 'all' })

          const rowsList = calls.map(call => transformCallRow(call))

          setProcessedReportData({
            rowsList,
            dispositionMap: {},
            timeString: 0
          })

          setReportsLoaded(true)
        } else {
          throw new Error('Failed to fetch calls from database')
        }

        // Use the same timezone-adjusted, exclusive-end-date boundaries as the
        // reports fetch above and the chart endpoint so the stat-card total and
        // the chart total always come from the same window.
        const metricsData = await fetchMetrics(
          adjustedStartDate.toISOString(),
          adjustedEndDateISO,
          campaignFilter
        )

        setMetrics(metricsData)

        if (metricsData?.planMinutes) {
          setPlanSize(metricsData.planMinutes)
        }
      } catch (error) {
        logger.error('Error fetching reports and metrics:', error)
      } finally {
        setMetricsLoading(false)
      }
    }

    loadReportsAndMetrics()
  }, [startDate, endDate, company, campaignFilter])

  useEffect(() => {
    if (campaignFilter) {
      setActualCampaignName(campaignFilter)
    } else if (processedReportData.rowsList && processedReportData.rowsList.length > 0) {
      const firstRow = processedReportData.rowsList[0]

      if (firstRow && firstRow.length > 5) {
        if (firstRow[5] && String(firstRow[5]).trim() !== '') {
          setActualCampaignName(String(firstRow[5]).trim())
        } else if (firstRow[2] && String(firstRow[2]).trim() !== '') {
          setActualCampaignName(String(firstRow[2]).trim())
        }
      }
    }
  }, [processedReportData.rowsList, campaignFilter])

  const refreshMetricsData = useCallback(async () => {
    try {
      setMetricsLoading(true)

      // Use the same timezone-adjusted, exclusive-end-date boundaries as the
      // initial load so a manual refresh returns the identical window.
      const adjustedStartDate = moment(startDate).tz(dateFormats.timezone).startOf('day').toDate()
      const adjustedEndDateISO = toExclusiveEndDateISO(endDate)

      const metricsData = await fetchMetrics(
        adjustedStartDate.toISOString(),
        adjustedEndDateISO,
        campaignFilter
      )

      setMetrics(metricsData)

      if (metricsData?.planMinutes) {
        setPlanSize(metricsData.planMinutes)
      }

      const chartData = await fetchChartData(
        startDateString,
        endDateString,
        campaignFilter
      )

      setChartData(chartData)

      return metricsData
    } catch (error) {
      logger.error('Error refreshing metrics:', error)
      return null
    } finally {
      setMetricsLoading(false)
    }
  }, [startDate, endDate, startDateString, endDateString, campaignFilter])

  const isDataLoading = !reportsLoaded || metricsLoading

  return {
    planSize,
    planSizeLoading,
    metrics,
    metricsLoading,
    chartData,
    processedReportData,
    actualCampaignName,
    isDataLoading,
    refreshMetricsData
  }
}

export default useDashboardData
